/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useState } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { ticketSurfaces } from '../../../shared/ticket/ticketSurfaces.styles';
import {
  effectivePosSellableQuantity,
  isProductCardOutOfStock,
} from '../../application/posScreen.utils';
import { formatPosMoney } from '../../application/posMoneyFormat';
import type { PosStockSalePolicy } from '../../domain/posTypes';
import { isComboValidityOutOfWindow, type ProductSearchRow } from '../../../catalog/domain-api';
import { useHasBusinessCapability } from '../../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { useTabWorkspaceLayout } from '../../../shared/layout/useContentLayout';
import { PosProductFavoriteStar } from './PosProductFavoriteStar';

type PosProductGridItemProps = {
  product: ProductSearchRow;
  canEdit: boolean;
  onPress: (product: ProductSearchRow) => void;
  /** Marcador en ficha: marcar/quitar favorito (rail). */
  onToggleFavorite?: (product: ProductSearchRow) => void;
  isFavorite?: boolean;
  /** Badges del rail: Combo / Oferta. */
  badges?: { combo?: boolean; promo?: boolean };
  stockSalePolicy: PosStockSalePolicy;
  addedFlash?: boolean;
};

function resolveStockLine(
  product: ProductSearchRow,
): { qtyText: string; qtyColor: string; suffix: string } | null {
  if (product.itemType === 'service') {
    return { qtyText: '', qtyColor: ticketColors.ink3, suffix: 'Servicio' };
  }
  if (product.isCombo) {
    const sell = effectivePosSellableQuantity(product);
    if (sell == null) {
      return { qtyText: '', qtyColor: ticketColors.ink3, suffix: 'Combo · partes' };
    }
    if (sell <= 0) {
      return { qtyText: '0', qtyColor: ticketColors.bad, suffix: 'sin stock' };
    }
    return {
      qtyText: String(Math.floor(sell)),
      qtyColor: ticketColors.ink3,
      suffix: 'vendibles',
    };
  }
  const stockQuantity = product.stockQuantity;
  const stockMinQuantity = product.stockMinQuantity;
  const hasStockValue = typeof stockQuantity === 'number';
  const hasStockRecord = product.stockId != null;
  const isMissingStockInfo = !hasStockValue || !hasStockRecord;
  const isOutOfStock = isMissingStockInfo || stockQuantity <= 0;
  if (isOutOfStock) {
    return { qtyText: '0', qtyColor: ticketColors.bad, suffix: 'sin stock' };
  }
  const isLowStock = typeof stockMinQuantity === 'number' && stockQuantity <= stockMinQuantity;
  return {
    qtyText: String(stockQuantity),
    qtyColor: isLowStock ? ticketColors.warn : ticketColors.ink3,
    suffix: 'en stock',
  };
}

/** Proto `.tile` — SoftPressable con hover soft-teal. */
const tileStyles = StyleSheet.create({
  card: {
    ...ticketSurfaces.paperSheet,
    borderRadius: 10,
    flex: 1,
    position: 'relative',
    paddingHorizontal: 12,
    paddingTop: 12,
    paddingBottom: 10,
    minHeight: 76,
    marginBottom: spacing.sm,
    ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as object) : null),
  },
  cardFlash: {
    borderColor: ticketColors.brandBorderStrong,
    backgroundColor: ticketColors.brandSoft,
  },
  cardWarn: {
    borderColor: ticketColors.warnBorder,
    backgroundColor: ticketColors.warnBg,
  },
  /** Hover Square: brand-soft bg + teal border (proto `.tile:hover`). */
  cardHover: {
    backgroundColor: ticketColors.brandSoft,
    borderColor: ticketColors.brandBorderStrong,
  },
  cardPressed: {
    transform: [{ scale: 0.985 }],
  },
  body: {
    flex: 1,
    gap: 4,
  },
  name: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    lineHeight: 16,
  },
  footRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 8,
    marginTop: 'auto',
  },
  price: {
    fontSize: 16,
    fontWeight: '700',
    color: ticketColors.brandInk,
    fontFamily: ticketFonts.mono,
    fontVariant: ['tabular-nums'],
    letterSpacing: -0.3,
    // El precio nunca se trunca: en web flex-shrink default es 1 y la
    // card lo comprimía ("$9.5…"). El stock es el flexible (flexShrink: 1).
    flexShrink: 0,
  },
  stockLine: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flexShrink: 1,
  },
  stockQty: {
    fontSize: 11,
    fontWeight: '500',
    fontFamily: ticketFonts.sans,
  },
  stockSuffix: {
    fontSize: 11,
    fontWeight: '500',
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  tagsRow: {
    flexDirection: 'row',
    gap: 4,
    flexWrap: 'wrap',
  },
  tag: {
    fontSize: 9,
    fontWeight: '700',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    fontFamily: ticketFonts.sans,
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 5,
    overflow: 'hidden',
  },
  tagCombo: { backgroundColor: ticketColors.okBg, color: ticketColors.ok },
  tagPromo: { backgroundColor: ticketColors.warnBg, color: ticketColors.warn },
  tileWrap: {
    flex: 1,
  },
  starAnchor: {
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 2,
  },
});

function PosProductGridItemInner({
  product,
  canEdit,
  onPress,
  onToggleFavorite,
  isFavorite = false,
  badges,
  stockSalePolicy,
  addedFlash = false,
}: PosProductGridItemProps) {
  const [hovered, setHovered] = useState(false);
  const inventoryStockUxEnabled = useHasBusinessCapability('inventory');
  const { layoutWide } = useTabWorkspaceLayout();
  const favoriteControl = canEdit && onToggleFavorite;
  const showFavoriteOutline = favoriteControl && !isFavorite;
  const starSubdued = layoutWide && !isFavorite && !hovered;
  const outOfStock = inventoryStockUxEnabled && isProductCardOutOfStock(product);
  const pausedToday = product.menuAvailable === false;
  const comboExpired = isComboValidityOutOfWindow(product);
  const stockBlocked = outOfStock && stockSalePolicy === 'block';
  const stockWarn = outOfStock && stockSalePolicy === 'warn';
  const interactive = canEdit && !pausedToday && !comboExpired;
  const accessibilityAction = stockBlocked ? 'Ver aviso de stock para' : 'Agregar';
  const stockLine = inventoryStockUxEnabled ? resolveStockLine(product) : null;

  return (
    <View style={tileStyles.tileWrap}>
      <Pressable
        style={({ pressed }) => [
          tileStyles.card,
          (stockBlocked || pausedToday || comboExpired) && { opacity: 0.78 },
          stockWarn && tileStyles.cardWarn,
          addedFlash && tileStyles.cardFlash,
          !canEdit && { opacity: 0.55 },
          interactive && hovered && !pressed && tileStyles.cardHover,
          interactive && pressed && tileStyles.cardPressed,
          !interactive && Platform.OS === 'web' ? ({ cursor: 'default' } as object) : null,
        ]}
        onPress={() => onPress(product)}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        disabled={!interactive}
        accessibilityRole="button"
        accessibilityLabel={`${accessibilityAction} ${product.name}${
          stockBlocked ? ', sin stock disponible' : stockWarn ? ', agotado, agregar con aviso' : ''
        }`}
        testID={`pos.product.add.${product.id}`}
      >
        {favoriteControl ? (
          <View style={tileStyles.starAnchor}>
            <PosProductFavoriteStar
              isFavorite={isFavorite}
              showOutline={showFavoriteOutline || isFavorite}
              subdued={starSubdued}
              onPress={() => onToggleFavorite(product)}
              onHoverIn={() => setHovered(true)}
              testID={`pos.product.fav.${product.id}`}
            />
          </View>
        ) : null}
      <View style={tileStyles.body}>
        <Text
          style={[tileStyles.name, favoriteControl ? { paddingRight: 28 } : null]}
          numberOfLines={2}
          ellipsizeMode="tail"
          {...(Platform.OS === 'web' ? ({ title: product.name } as object) : null)}
        >
          {product.name}
        </Text>
        {badges && (badges.combo || badges.promo) ? (
          <View style={tileStyles.tagsRow}>
            {badges.combo ? <Text style={[tileStyles.tag, tileStyles.tagCombo]}>Combo</Text> : null}
            {badges.promo ? <Text style={[tileStyles.tag, tileStyles.tagPromo]}>Oferta</Text> : null}
          </View>
        ) : null}
        <View style={tileStyles.footRow}>
          <Text style={tileStyles.price} numberOfLines={1}>
            {formatPosMoney(product.price)}
          </Text>
          {pausedToday ? (
            <Text style={[tileStyles.stockSuffix, { color: ticketColors.warn }]} numberOfLines={1}>Pausado hoy</Text>
          ) : comboExpired ? (
            <Text style={[tileStyles.stockSuffix, { color: ticketColors.warn }]} numberOfLines={1}>Fuera de vigencia</Text>
          ) : stockLine ? (
            <View style={tileStyles.stockLine}>
              {stockLine.qtyText ? (
                <Text style={[tileStyles.stockQty, { color: stockLine.qtyColor }]}>
                  {stockLine.qtyText}
                </Text>
              ) : null}
              <Text style={tileStyles.stockSuffix} numberOfLines={1}>{stockLine.suffix}</Text>
            </View>
          ) : null}
        </View>
      </View>
      </Pressable>
    </View>
  );
}

export const PosProductGridItem = React.memo(PosProductGridItemInner);
